import {
    createContext,
    useContext,
    useEffect,
    // useContext,
    // useEffect, 
    useState
} from 'react'
// import { useToken } from '@/services/AuthService'
import { Token } from '../common/token.model'
import { HttpServiceContext } from './HttpProvider'
import { MuiLogin } from '../pages/layout/MuiLogin'
import { useConfig } from '../config/ConfigProvider'
import { jwtDecode } from 'jwt-decode'
import { useCookies } from 'react-cookie'
import { set } from 'lodash'
import { getUnifiedAccessToken, LoginOptions } from '../auth/getAccessToken';
import { useOAuth } from '../auth/OAuthProvider';
import { logout as oauthLogout } from '../auth/oauthService';
import { AuthenticationResult } from '@azure/msal-browser';

// import { HttpServiceContext } from './HttpProvider'
interface AccessProviderProps {
    children: React.ReactNode
}

const initialToken: Token = {
    exp: 0,
    nbf: 0,
    ver: '',
    iss: '',
    sub: '',
    aud: '',
    acr: '',
    nonce: '',
    iat: 0,
    auth_time: 0,
    email: '',
    displayName: '',
    preferred_username: '',
    givenName: '',
    surname: '',
    "alternativeSecurityId": "",
    "accessToken": "",
    "family_name": "", // used as google profile picture
    "name": "", // full name
    auth_method: undefined,
    auth_source: undefined,
    salesforce_user_id: undefined
}

type SalesforceExtraLoginInfo = {
    userId: string,
    email?: string,
    taskId?: string,
    conversationId?: string,
    type: "salesforceUser"
}

export type AccessProviderState = {
    accessToken: string | null,
    decodedToken: Token | null,
    handleLogin: (domainHint?: string, interactionMode?: "popup" | "redirect", loginHint?: string, extraLoginInfo?: SalesforceExtraLoginInfo, idp?: string) => Promise<AuthenticationResult | void>
    handleLogout: () => Promise<void>,
    extraLoginInfo?: SalesforceExtraLoginInfo,
    trySalesforceSSO: (loginHint: string, loginContext: SalesforceExtraLoginInfo, domainHint?: string) => Promise<void>
}

const state: AccessProviderState = {
    accessToken: null,
    decodedToken: null,
    handleLogin: async () => { },
    handleLogout: async () => { },
    trySalesforceSSO: async () => { },
    extraLoginInfo: undefined
}

export const AccessContext = createContext<AccessProviderState>(state)

export default function AccessProvider({ children }: AccessProviderProps) {
    console.log('🎯 AccessProvider rendering...');
    const [value, setValue] = useState<AccessProviderState>(state);
    const { httpService } = useContext(HttpServiceContext)
    // Note: MSAL removed - now using OAuth cookie-based authentication via OAuthProvider
    const config = useConfig();
    const [needsTimeout, setNeedsTimeout] = useState(false);
    const [authCookie, setCookie, removeCookie] = useCookies(['auth'])
    
    // Get OAuth user to determine if authenticated
    const { user: oauthUser, loading: oauthLoading } = useOAuth();
    const isAuthenticated = !!value.accessToken || !!oauthUser;
    
    console.log('🎯 AccessProvider - accessToken:', !!value.accessToken, 'oauthUser:', oauthUser ? oauthUser.email : 'null', 'isAuthenticated:', isAuthenticated);

    // Note: handleLogin is deprecated - OAuth flow now handled by OAuthProvider
    // This stub remains for backward compatibility with existing code
    const handleLogin: AccessProviderState["handleLogin"] = async (
        domainHint, interactionMode = "popup", loginHint, extraLoginInfo, idp
    ) => {
        console.warn('AccessProvider.handleLogin is deprecated - use OAuth dialog instead');
        // For Salesforce server auth, preserve this functionality
        if (extraLoginInfo) {
            try {
                await handleSalesforceServerAuth(extraLoginInfo);
            } catch (error) {
                throw error;
            }
        }
    };

    const handleLogout = async () => {
        console.log('🔴 AccessProvider.handleLogout - Starting logout...');
        
        // Check if user was authenticated via backend Salesforce method
        // We check both the token claim and presence of app_jwt in localStorage
        const isBackendAuth = value.decodedToken?.auth_method === "salesforce_custom" || 
                              localStorage.getItem("app_jwt") !== null;
        
        try {
            // Call backend logout endpoint to revoke refresh tokens and clear cookies
            console.log('🔴 Calling OAuth logout service...');
            await oauthLogout(config.apiBaseUrl);
            console.log('✅ OAuth logout service completed');
            
            // Clear local storage items that might store auth info
            localStorage.removeItem('auth');
            localStorage.removeItem('app_jwt');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('msal.account.keys');
            localStorage.removeItem('msal.token.keys');
            
            // Clear any session storage items
            sessionStorage.removeItem('auth');
            sessionStorage.clear();
            
            // Clear the auth cookie
            removeCookie('auth');
            
            // Clear httpService token
            httpService?.setToken('');
            
            // Clear application state
            setValue(state => ({
                ...state,
                accessToken: null,
                decodedToken: null,
                extraLoginInfo: undefined
            }));
            
            console.log('✅ Local state cleared');
        } catch (error) {
            console.error('❌ Error during logout:', error);
            // Fallback: clear everything locally even if remote logout fails
            localStorage.removeItem('auth');
            localStorage.removeItem('app_jwt');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('msal.account.keys');
            localStorage.removeItem('msal.token.keys');
            sessionStorage.clear();
            removeCookie('auth');
            
            // Clear httpService token
            httpService?.setToken('');
            
            setValue(state => ({
                ...state,
                accessToken: null,
                decodedToken: null,
                extraLoginInfo: undefined
            }));
        }
        
        // Redirect to login page
        console.log('🔴 Redirecting to /login');
        window.location.href = '/login';
    };

    const trySalesforceSSO = async (
        loginHint: string,
        loginContext: SalesforceExtraLoginInfo,
        domainHint?: string
    ) => {
        try {
            // For external IdPs, skip silent auth to avoid iframe issues
            const isExternalIdP = domainHint && 
                (domainHint.includes('google.com') || domainHint.includes('salesforce.com'));
            
            if (!isExternalIdP) {
                // First try silent authentication only for internal IdPs
                await handleLogin(domainHint, "redirect", loginHint, loginContext);
                return; // Success, exit early
            }
        } catch (error: any) {
            // Check for specific iframe-related errors
            const isIframeError = 
                error.errorCode === "monitor_window_timeout" ||
                error.message?.includes("iframe") ||
                error.message?.includes("navigation") ||
                error.message?.includes("sandboxed");
        }
        
        try {
            // Try interactive login (this will work for external IdPs)
            await handleLogin(domainHint, "redirect", loginHint, loginContext);
        } catch (error: any) {
            const isTimeoutError = error.errorCode === "monitor_window_timeout";
            
            if (isTimeoutError) {
                // For timeout errors, fallback to server auth
                await handleSalesforceServerAuth(loginContext);
            } else {
                // For other errors, propagate
                throw error;
            }
        }
    };

    // Server-side Salesforce authentication (bypasses MSAL for iframe scenarios)
    const handleSalesforceServerAuth = async (salesforceData: SalesforceExtraLoginInfo) => {
        try {
            // Use ApiService instead of direct fetch
            const data = await httpService.salesforceLogin({
                userId: salesforceData.userId,
                email: salesforceData.email,
                taskId: salesforceData.taskId
            });

            // CRITICAL FIX: Use the custom token, NOT the Azure AD app token!
            // The azureToken is for Microsoft Graph API calls, not for your own API
            const tokenToUse = data.token; // Use custom token with correct audience
            
            // Store the token and update state
            localStorage.setItem("app_jwt", tokenToUse);
            setCookie('auth', tokenToUse);
            const decodedToken: Token = jwtDecode(tokenToUse);
            setValue(state => ({
                ...state,
                decodedToken,
                accessToken: tokenToUse,
                extraLoginInfo: salesforceData
            }));
            
            // Ensure httpService gets the token
            if (httpService) {
                httpService.setToken(tokenToUse);
            }
            
            setNeedsTimeout(true);
        } catch (error) {
            throw error;
        }
    };

    // Check if we should skip OIDC federation for specific domains with known issues
    const shouldSkipOIDCFederation = (salesforceData: SalesforceExtraLoginInfo) => {
        // Skip OIDC for specific domains that are known to have federation issues
        const problematicDomains = ['theclarityteam.net', 'clarity.com'];
        if (salesforceData.email && problematicDomains.some(domain => salesforceData.email!.includes(domain))) {
            return true;
        }
        
        return false;
    };

    // Hybrid authentication handler for Salesforce users
    const handleSalesforceAuth = async (salesforceData: SalesforceExtraLoginInfo) => {
        const shouldSkipOIDC = shouldSkipOIDCFederation(salesforceData);
        
        // Note: MSAL silent auth removed - now using OAuth
        // Skip directly to backend authentication
        
        // 2. If we should skip OIDC, go straight to backend authentication
        if (shouldSkipOIDC) {
            try {
                await handleSalesforceServerAuth(salesforceData);
                return;
            } catch (backendError) {
                throw backendError;
            }
        }
        
        // Note: MSAL OIDC federation removed - now using OAuth
        // Skip directly to backend authentication
        
        // 4. Final fallback to backend method
        try {
            await handleSalesforceServerAuth(salesforceData);
        } catch (backendError) {
            throw backendError;
        }
    };

    useEffect(() => {
        setValue(state => ({
            ...state,
            handleLogin,
            handleLogout,
            trySalesforceSSO
        }));
    }, []);

    // Note: MSAL redirect handling removed - OAuth handles auth via backend cookies
    // The OAuthProvider in index.tsx now manages authentication state

    useEffect(() => {
        if (authCookie.auth && !value.accessToken) {
            const decodedToken: Token = jwtDecode(authCookie.auth);

            setValue(state => {
                return {
                    ...state,
                    decodedToken: decodedToken,
                    accessToken: authCookie.auth
                };
            });
            httpService?.setToken(authCookie.auth);
            setNeedsTimeout(true);
        } else if (!authCookie.auth) {
            setValue((state) => ({
                ...state,
                accessToken: null,
                decodedToken: null
            }));
            setNeedsTimeout(false);
        }
    }, [authCookie.auth, value.accessToken]);

    //Add Token refresh logic here
    const refreshToken = async (forceRefresh: boolean = false) => {
        try {
            const accessToken = await getUnifiedAccessToken({
                scopes: ["openid", "profile", "email", config.microsoft_auth],
                forceRefresh,
            });
            const decodedToken: Token = jwtDecode(accessToken);

            if (accessToken) {
                setCookie('auth', accessToken);
            } else {
                // Handle error
                return;
            }

            setValue(state => ({
                ...state,
                decodedToken: decodedToken as Token,
                accessToken: accessToken ?? ""
            }));

            httpService?.setToken(accessToken);
            setNeedsTimeout(true);

            if (!decodedToken?.family_name) {
                setTimeout(() => {
                    refreshToken(true);
                }, 5000);
            }
        } catch (error) {
            // Optionally handle error (e.g., force logout or re-login)
        }
    };

    // Add this method to AccessProvider
    const refreshTokenIfNeeded = async () => {
        const token = localStorage.getItem("app_jwt");
        if (!token) return;
        
        try {
          const payload = token.split(".")[1];
          const decoded = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
          const exp = decoded.exp * 1000;
          const now = Date.now();
          const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
          
          if (now >= (exp - bufferTime)) {
            const salesforceUserId = decoded.salesforce_user_id;
            const email = decoded.email;
            
            if (salesforceUserId && email) {
              await handleSalesforceServerAuth({ userId: salesforceUserId, email: email, type: "salesforceUser" });
            }
          }
        } catch (error) {
          // Error checking token expiration
        }
      };

    // Add automatic token refresh check
    useEffect(() => {
        // Only set up interval if we have backend auth token
        const hasBackendToken = localStorage.getItem("app_jwt");
        if (!hasBackendToken) return;
        
        const interval = setInterval(() => {
            refreshTokenIfNeeded();
        }, 60000); // Check every minute
        
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (value.decodedToken?.exp && value.accessToken && needsTimeout) {
            const exp = (value.decodedToken.exp || 0) * 1000;
            const now = Date.now();

            // calculate how long until token expires
            const expiresIn = exp - now;

            // make expires in common language, like 30min/15min
            const minutes = Math.floor(expiresIn / 60000);

            if (expiresIn < 0) {
                setNeedsTimeout(false);
                removeCookie('auth');
                handleLogin();
            } else if (minutes < 5 && localStorage.getItem("app_jwt")) {
                // Only refresh if we have a backend token (avoid MSAL token refresh loops)
                refreshTokenIfNeeded();
            } else if (minutes >= 5) {
                // Set timeout for future refresh, but only for backend tokens
                if (localStorage.getItem("app_jwt")) {
                    setTimeout(() => {
                        refreshTokenIfNeeded();
                    }, expiresIn - 300000); // Refresh 5 minutes before expiration
                }
                setNeedsTimeout(false);
            }
        }
    }, [value.decodedToken?.exp, needsTimeout])

    // Salesforce iframe message handler
    useEffect(() => {
        const handler = async (event: MessageEvent) => {
            const { type, userId, email, taskId, conversationId } = event.data || {};
            
            if (type === "salesforceUser" && userId) {
                const salesforceData: SalesforceExtraLoginInfo = {
                    userId,
                    email,
                    taskId,
                    conversationId,
                    type: "salesforceUser"
                };
                
                try {
                    await handleSalesforceAuth(salesforceData);
                } catch (error) {
                    // Optionally show error to user
                }
            }
        };
        
        window.addEventListener("message", handler);
        return () => window.removeEventListener("message", handler);
    }, [config.microsoft_auth]);

    useEffect(() => {
        // detect if browser is in dark mode: Safari, Chrome, Firefox, Edge
        const darkModeMediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

        sessionStorage.setItem("themeMode", darkModeMediaQuery.matches ? 'dark' : 'light');
        sessionStorage.setItem("systemControlledTheme", 'true');

        setTimeout(() => {
            window.dispatchEvent(new Event("storage")); // Notify other tabs
        });

        const handler = (e: MediaQueryListEvent) => {
            const newColorScheme = e.matches ? "dark" : "light";
            const controlled = sessionStorage.getItem("systemControlledTheme");
            // If system matches app theme
            if (controlled === 'true') {
                sessionStorage.setItem("themeMode", newColorScheme);
                setTimeout(() => {
                    window.dispatchEvent(new Event("storage")); // Notify other tabs
                });
            }
        }

        darkModeMediaQuery.addEventListener("change", handler);

        return () => darkModeMediaQuery.removeEventListener("change", handler);
    }, []);

    return (
        <>
            <>
                {/** Login Screen goes here */}
            </>
            <AccessContext.Provider value={value}>{isAuthenticated ? children : <MuiLogin />}</AccessContext.Provider>
        </>
    )
}
